/**
 * Global "is this <select> popup open" tracker.
 *
 * Native <select> elements have no JS event that fires when their
 * dropdown popup opens, and they keep `:focus` after the user picks
 * — which means a CSS `:focus` rule can't tell "popup open" from
 * "select has focus but popup is closed". This module makes up the
 * gap with a single delegated listener:
 *
 *   - mousedown on a <select>    → mark it open (`data-open="true"`)
 *   - keydown Space/Enter/Alt+↓  → mark it open (keyboard openers)
 *   - change                      → popup closed by selection → mark closed
 *   - blur                        → popup closed by clicking away → mark closed
 *   - keydown Escape              → user dismissed → mark closed
 *
 * The CSS rule in `index.css` reads `select[data-open="true"]` and
 * rotates the sibling chevron icon accordingly. Side-effects only —
 * no state is owned here.
 *
 * Imported once from `main.jsx` so it runs at app boot. Idempotent:
 * if Vite HMR re-imports this module the listeners are removed and
 * re-attached.
 */

const OPEN_ATTR = 'data-open'

function markOpen(el) {
  if (el && el.tagName === 'SELECT') el.setAttribute(OPEN_ATTR, 'true')
}
function markClosed(el) {
  if (el && el.tagName === 'SELECT') el.removeAttribute(OPEN_ATTR)
}

function onMouseDown(e) {
  // Clicking the select toggles the popup. If it was already open
  // (user clicked the chevron again to close), treat as close.
  const sel = e.target.closest?.('select')
  if (!sel) return
  if (sel.hasAttribute(OPEN_ATTR)) markClosed(sel)
  else markOpen(sel)
}

function onKeyDown(e) {
  if (e.target?.tagName !== 'SELECT') return
  const sel = e.target
  // Keyboard openers: Space, Enter, Alt+ArrowDown / Alt+ArrowUp open
  // the native popup in every browser we care about.
  if (
    e.key === ' ' ||
    e.key === 'Enter' ||
    (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp'))
  ) {
    markOpen(sel)
    return
  }
  if (e.key === 'Escape' || e.key === 'Tab') {
    markClosed(sel)
  }
}

function onChange(e) {
  // Selection commits the popup — even if the chosen option matches
  // the previous value, the popup is closed at this point. Also blur
  // the select so its `:focus` state matches, which keeps any other
  // focus-driven styling consistent.
  if (e.target?.tagName !== 'SELECT') return
  markClosed(e.target)
}

function onBlur(e) {
  if (e.target?.tagName !== 'SELECT') return
  markClosed(e.target)
}

let installed = false

export function installDropdownArrowGlue() {
  if (installed) return uninstallDropdownArrowGlue
  installed = true
  // `mousedown` and `keydown` need to run before native default so
  // capture-phase isn't required, but `blur` doesn't bubble — we
  // listen in capture phase so the delegated handler still fires.
  document.addEventListener('mousedown', onMouseDown, true)
  document.addEventListener('keydown', onKeyDown, true)
  document.addEventListener('change', onChange, true)
  document.addEventListener('blur', onBlur, true)
  return uninstallDropdownArrowGlue
}

export function uninstallDropdownArrowGlue() {
  if (!installed) return
  installed = false
  document.removeEventListener('mousedown', onMouseDown, true)
  document.removeEventListener('keydown', onKeyDown, true)
  document.removeEventListener('change', onChange, true)
  document.removeEventListener('blur', onBlur, true)
}

// Auto-install on import. Convenient and safe — main.jsx just needs
// to import the module once.
installDropdownArrowGlue()

// Vite HMR cleanup: when this module is replaced, remove the listeners
// from the old copy so we don't end up with two handlers in dev.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    uninstallDropdownArrowGlue()
  })
}
